import { FC, Suspense, lazy, useEffect } from 'react';
import { StatsigProvider } from '@statsig/react-bindings';
import Spinner from './ui/spinner/spinner';
import { DocumentMetaProvider } from './contexts/document-meta-context';
import { metaData as selectionScreenMeta } from './routes/selection-screen/selection-screen.constants';
import usercentrics from './services/usercentrics';
import setStorage from './utils/browser-storage/storage';
import { storageKeys } from './utils/browser-storage/storage-keys';
import globalDebug from './utils/debugger';
import errorTracker from './utils/error-tracker';
import initInterceptors from './utils/interceptor/init-interceptors';
import isMobileAppWebview from './utils/is-mobile-app-webview';
import isPrerender from './utils/is-prerender';
import isProduction from './utils/is-production';
import isUITest from './utils/is-ui-test';
import lastVisitedWorld from './utils/last-visited-world';
import prerenderMeta from './utils/prerender-meta';
import { initQualityEventsDebug } from './utils/quality-events-debug';

// Disable showing consoles (log, warn, info, error) on the production
globalDebug(!isProduction(), ['warn']);

errorTracker.launch();
setStorage();
initInterceptors();
initQualityEventsDebug();
window.enhancedConversionInfo = {}; // additional info for enhanced conversions in Adwords
window.statsigVariant = {};

const SelectionScreen = lazy(() => import('./routes/selection-screen/selection-screen'));
const App = lazy(() => import('./App'));

const Entry: FC = () => {
    const htmlTag = document.getElementsByTagName('html')[0];
    const lastWorld = lastVisitedWorld.get();
    const storedStatsigUser = window.storage.getItem(storageKeys.user.statsig);
    const statsigUser: StatsigUser = storedStatsigUser ? JSON.parse(storedStatsigUser) : {};

    htmlTag.setAttribute('lang', 'en');

    useEffect(() => {
        setTimeout(() => {
            prerenderMeta.set(200);
        }, 15000);

        const href = decodeURIComponent(window.location.href);

        if (href.indexOf('query=?utm_source') > -1) {
            window.location.href = href.replace('query=?', 'query=&');
        }

        // Init Usercentrics service
        // Skip it for tests and pre-rendering
        if (!isUITest() && !isPrerender() && !isMobileAppWebview()) {
            usercentrics.init();
        }
    }, []);

    return (
        <Suspense fallback={<div />}>
            <StatsigProvider
                sdkKey={String(process.env.REACT_APP_STATSIG_CLIENT_API_KEY)}
                user={statsigUser}
                options={{
                    environment: { tier: isProduction() ? 'production' : 'staging' },
                    loggingEnabled: isPrerender() || isUITest() ? 'disabled' : 'browser-only'
                }}
                loadingComponent={<Spinner />}
            >
                {window.location.pathname !== '/' && <App />}
                {window.location.pathname === '/' && (
                    <>
                        {lastWorld && <App />}
                        {!lastWorld && (
                            <DocumentMetaProvider meta={() => selectionScreenMeta}>
                                <SelectionScreen />
                            </DocumentMetaProvider>
                        )}
                    </>
                )}
            </StatsigProvider>
        </Suspense>
    );
};

export default Entry;
